import axios from 'axios';
import router from '@/router';
import {
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGIN_ERROR,
  SIGNUP_REQUEST,
  SIGNUP_SUCCESS,
  SIGNUP_ERROR,
  LOGOUT,
  RESTORE_AUTH,
  RESTORE_AUTH_REQUEST,
} from './types';
import { GET_BOARDS_SUCCESS, CLEAR_BOARD_STATE } from '@/store/boards/types';
import { GET_OWNERS_SUCCESS, CLEAR_OWNER_STATE } from '@/store/owners/types';

const apiBaseURL = process.env.VUE_APP_API_URL;

const clearPrivateData = (commit) => {
  commit(GET_BOARDS_SUCCESS, { data: [] });
  commit(CLEAR_BOARD_STATE);
  commit(GET_OWNERS_SUCCESS, { data: [] });
  commit(CLEAR_OWNER_STATE);
};

const persistSession = (profile) => {
  const role = profile.role || (profile.is_admin ? 'admin' : 'operator');
  localStorage.setItem('authToken', profile.token);
  localStorage.setItem('role', role);
  if (role === 'operator' && profile.operator_id != null) {
    localStorage.setItem('operator_id', String(profile.operator_id));
  } else {
    localStorage.removeItem('operator_id');
  }
  if (role === 'admin') localStorage.setItem('isAdmin', 'true');
  else localStorage.removeItem('isAdmin');
  axios.defaults.headers.common.Authorization = `Bearer ${profile.token}`;
};

const goHome = async () => {
  if (router.currentRoute.name === 'Home') return;
  await router.replace({ name: 'Home' }).catch((error) => {
    if (error.name !== 'NavigationDuplicated' && error.name !== 'NavigationCancelled') throw error;
  });
};

const login = async ({ commit }, { pin }) => {
  commit(LOGIN_REQUEST);
  try {
    const { data } = await axios.post(`${apiBaseURL}/login`, { pin }, { timeout: 15000 });
    clearPrivateData(commit);
    persistSession(data.data);
    commit(LOGIN_SUCCESS, data);
    return data;
  } catch (error) {
    commit(LOGIN_ERROR, error);
    throw error;
  }
};

const logout = async ({ commit }, { localOnly = false } = {}) => {
  const headers = axios.defaults.headers.common;
  const authorization = headers.Authorization || headers.authorization;
  // Remove private lists before changing identity or beginning navigation/network work.
  clearPrivateData(commit);
  delete headers.Authorization;
  delete headers.authorization;
  ['authToken', 'isAdmin', 'role', 'operator_id'].forEach((key) => localStorage.removeItem(key));
  commit(LOGOUT);

  const revoke = !localOnly && authorization
    ? axios.post(`${apiBaseURL}/logout`, {}, {
      headers: { Authorization: authorization },
      timeout: 5000,
    }).catch(() => {})
    : Promise.resolve();
  await Promise.all([goHome(), revoke]);
};

const restoreAuth = async ({ state, commit, dispatch }) => {
  const token = state.authToken;
  if (!token) return;
  commit(RESTORE_AUTH_REQUEST);
  try {
    const { data } = await axios.get(`${apiBaseURL}/me`, { timeout: 10000 });
    if (state.authToken !== token) return;
    const profile = { ...data.data, token };
    if (!['admin', 'operator'].includes(profile.role)) throw new Error('Invalid session role');
    clearPrivateData(commit);
    persistSession(profile);
    commit(LOGIN_SUCCESS, { data: profile });
  } catch (error) {
    if (state.authToken === token) await dispatch(LOGOUT, { localOnly: true });
    throw error;
  }
};

const signup = async ({ commit }, userData) => {
  commit(SIGNUP_REQUEST);
  try {
    const { data } = await axios.post(`${apiBaseURL}/register`, {
      name: userData.firstName,
      surname: userData.lastName,
      plot_number: userData.houseNumber,
      email: userData.email,
      phone: userData.phone,
    });
    commit(SIGNUP_SUCCESS, data);
    return data;
  } catch (error) {
    commit(SIGNUP_ERROR, error);
    throw error;
  }
};

export default {
  [LOGIN_REQUEST]: login,
  [SIGNUP_REQUEST]: signup,
  [LOGOUT]: logout,
  [RESTORE_AUTH]: restoreAuth,
};
